import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CalendarClock, Sun, Sunset, Clock } from "lucide-react";

const WD = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

// Períodos permitidos conforme o dia da semana:
// Seg–Sex: Manhã 09–12 / Tarde 13–17 · Sábado: Manhã 09–12 / Tarde 13–16 · Domingo: só Manhã 09–12
export function periodsFor(dateStr) {
  if (!dateStr) return [];
  const wd = new Date(dateStr + "T00:00:00").getDay();
  if (wd === 0) return [{ v: "manha", label: "Manhã (09h–12h)", icon: Sun }];
  if (wd === 6) return [
    { v: "manha", label: "Manhã (09h–12h)", icon: Sun },
    { v: "tarde", label: "Tarde (13h–16h)", icon: Sunset },
  ];
  return [
    { v: "manha", label: "Manhã (09h–12h)", icon: Sun },
    { v: "tarde", label: "Tarde (13h–17h)", icon: Sunset },
  ];
}

function composeAvailability(date, period, special) {
  if (!date) return "";
  const d = new Date(date + "T00:00:00");
  const dm = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  const head = `${WD[d.getDay()]} ${dm}`;
  if (period === "especial") return `${head} · Horário especial${special ? `: ${special}` : ""}`;
  const p = periodsFor(date).find((x) => x.v === period);
  return p ? `${head} · ${p.label}` : head;
}

export default function AvailabilityPicker({ allowPast = false, value, onChange, testidPrefix = "availability" }) {
  const date = value?.availability_date || "";
  const period = value?.availability_period || "";
  const special = value?.availability_special || "";
  const today = new Date().toISOString().slice(0, 10);

  const emit = (nextDate, nextPeriod, nextSpecial) => {
    onChange({
      availability_date: nextDate,
      availability_period: nextPeriod,
      availability_special: nextSpecial,
      availability: composeAvailability(nextDate, nextPeriod, nextSpecial),
    });
  };

  const options = periodsFor(date);

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 p-3" data-testid={`${testidPrefix}-block`}>
      <Label className="text-sm flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-[#1e7be0]" /> Disponibilidade para a visita *</Label>
      <div className="space-y-1.5">
        <Label className="text-xs">Data preferida</Label>
        <Input type="date" min={allowPast ? undefined : today} value={date}
          onChange={(e) => emit(e.target.value, "", special)} data-testid={`${testidPrefix}-date-input`} />
      </div>
      {date && (
        <div className="space-y-1.5">
          <Label className="text-xs">Período</Label>
          <div className="grid grid-cols-2 gap-2">
            {options.map((o) => {
              const Icon = o.icon;
              const active = period === o.v;
              return (
                <Button key={o.v} type="button" variant={active ? "default" : "outline"}
                  className={`gap-1.5 justify-start ${active ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                  onClick={() => emit(date, o.v, special)} data-testid={`${testidPrefix}-period-${o.v}`}>
                  <Icon className="h-4 w-4" /> {o.label}
                </Button>
              );
            })}
            <Button type="button" variant={period === "especial" ? "default" : "outline"}
              className={`gap-1.5 justify-start col-span-2 ${period === "especial" ? "bg-amber-500 hover:bg-amber-600" : "text-amber-700 border-amber-300 hover:bg-amber-50"}`}
              onClick={() => emit(date, "especial", special)} data-testid={`${testidPrefix}-period-especial`}>
              <Clock className="h-4 w-4" /> Preciso de horário especial
            </Button>
          </div>
        </div>
      )}
      {period === "especial" && (
        <div className="space-y-1.5">
          <Label className="text-xs">Descreva o horário especial</Label>
          <Textarea rows={2} value={special} placeholder="Ex: Somente após as 18h, ou almoço 12h–13h"
            onChange={(e) => emit(date, "especial", e.target.value)} data-testid={`${testidPrefix}-special-input`} />
        </div>
      )}
    </div>
  );
}
